import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import type { Camera, Site, LayoutId } from '../../types'
import { LAYOUT_PRESETS, LAYOUT_MAP, MAX_SLOTS } from '../../types'
import { useAuth } from '../../hooks/useAuth'
import { useViewerLayouts } from '../../hooks/useViewerLayouts'
import { useToast } from '../ui/Toast'
import { StreamSlot } from './StreamSlot'
import { StreamSidebar } from './StreamSidebar'

interface Props {
  site: Site
  cameras: Camera[]
  onPtzOpen: (camera: Camera) => void
  onRecordingOpen: (camera: Camera) => void
  onEditCamera: (camera: Camera) => void
  onSettingsOpen: (camera: Camera) => void
}

function bestFitGrid(cameraCount: number): LayoutId {
  if (cameraCount <= 1) return '1x1'
  if (cameraCount <= 4) return '2x2'
  if (cameraCount <= 9) return '3x3'
  if (cameraCount <= 16) return '4x4'
  if (cameraCount <= 25) return '5x5'
  return '6x6'
}

export function GridViewer({ site, cameras, onPtzOpen, onRecordingOpen, onEditCamera, onSettingsOpen }: Props) {
  const { profile } = useAuth()
  const { toast } = useToast()
  const {
    layouts, defaults, loading: layoutsLoading,
    saveLayout, updateLayout, deleteLayout,
    setDefault, clearDefault, getEffectiveDefault,
  } = useViewerLayouts(profile?.org_id, site.id, null)

  const storageKey = `rv-viewer-${site.id}`
  const [gridSize, setGridSize] = useState<LayoutId>(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw) {
        const saved = JSON.parse(raw)
        // Support legacy numeric values and new string IDs
        if (typeof saved.gridSize === 'number') {
          const map: Record<number, LayoutId> = { 1: '1x1', 2: '2x2', 3: '3x3', 4: '4x4' }
          if (map[saved.gridSize]) return map[saved.gridSize]
        }
        if (typeof saved.gridSize === 'string' && LAYOUT_MAP[saved.gridSize as LayoutId]) {
          return saved.gridSize as LayoutId
        }
      }
    } catch { /* ignore */ }
    return bestFitGrid(cameras.length)
  })

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [selectedSlot, setSelectedSlot] = useState(0)

  // slots[i] = camera ID or null
  const layout = LAYOUT_MAP[gridSize] ?? LAYOUT_MAP['2x2']
  const totalSlots = layout.totalSlots
  const [slots, setSlots] = useState<(string | null)[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw) {
        const saved = JSON.parse(raw)
        if (Array.isArray(saved.slots)) {
          // Pad or trim to MAX_SLOTS
          const padded = [...saved.slots]
          while (padded.length < MAX_SLOTS) padded.push(null)
          return padded.slice(0, MAX_SLOTS)
        }
      }
    } catch { /* ignore */ }
    // Auto-assign cameras in order on first load
    const initial: (string | null)[] = Array(MAX_SLOTS).fill(null)
    cameras.forEach((cam, i) => { if (i < MAX_SLOTS) initial[i] = cam.id })
    return initial
  })

  // View management state
  const [activeLayoutId, setActiveLayoutId] = useState<string | null>(null)
  const [showSaveForm, setShowSaveForm] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [saveVisibility, setSaveVisibility] = useState<'personal' | 'org'>('personal')
  const [saving, setSaving] = useState(false)

  // Auto-apply default layout on first load (only once)
  const defaultApplied = useRef(false)
  useEffect(() => {
    if (defaultApplied.current || layoutsLoading || !profile || layouts.length === 0) return
    defaultApplied.current = true

    // Skip if user already has a localStorage scratch
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw) {
        const saved = JSON.parse(raw)
        if (saved.fromLayout) return // already loaded from a saved view before
      }
    } catch { /* ignore */ }

    const defaultId = getEffectiveDefault(profile.id)
    if (defaultId) {
      const layout = layouts.find(l => l.id === defaultId)
      if (layout) {
        setActiveLayoutId(defaultId)
        setGridSize(layout.grid_size)
        setSlots(layout.slots)
      }
    }
  }, [layoutsLoading, layouts, profile, getEffectiveDefault, storageKey])

  // Persist to localStorage as scratch save
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ slots, gridSize }))
    } catch { /* quota exceeded — ignore */ }
  }, [slots, gridSize, storageKey])

  const assignedCameraIds = useMemo(() => {
    const set = new Set<string>()
    slots.forEach((id) => { if (id) set.add(id) })
    return set
  }, [slots])

  const cameraMap = useMemo(() => {
    const map = new Map<string, Camera>()
    cameras.forEach((c) => map.set(c.id, c))
    return map
  }, [cameras])

  const assignCamera = useCallback((cameraId: string, slotIndex?: number) => {
    setSlots((prev) => {
      const next = [...prev]
      const target = slotIndex ?? selectedSlot
      for (let i = 0; i < next.length; i++) {
        if (next[i] === cameraId) next[i] = null
      }
      next[target] = cameraId
      return next
    })
  }, [selectedSlot])

  const clearSlot = useCallback((index: number) => {
    setSlots((prev) => {
      const next = [...prev]
      next[index] = null
      return next
    })
  }, [])

  const handleToggleHd = useCallback((_camera: Camera, _isHd: boolean) => {
    // Stream provisioning now happens in the backend when playback starts.
  }, [])

  // Apply a saved layout
  const applyLayout = useCallback((layoutId: string) => {
    const layout = layouts.find(l => l.id === layoutId)
    if (!layout) return
    setActiveLayoutId(layoutId)
    setGridSize(layout.grid_size)
    setSlots(layout.slots)
  }, [layouts])

  // Save current layout as new view
  const handleSaveNew = useCallback(async () => {
    if (!saveName.trim() || !profile) return
    setSaving(true)
    try {
      const created = await saveLayout({
        name: saveName.trim(),
        org_id: profile.org_id!,
        created_by: profile.id,
        site_id: site.id,
        visibility: saveVisibility,
        grid_size: gridSize,
        slots,
      })
      setActiveLayoutId(created.id)
      setShowSaveForm(false)
      setSaveName('')
      toast(`View "${created.name}" saved`, 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to save view', 'error')
    } finally {
      setSaving(false)
    }
  }, [saveName, saveVisibility, profile, site.id, gridSize, slots, saveLayout, toast])

  // Update the active layout with current state
  const handleUpdate = useCallback(async () => {
    if (!activeLayoutId) return
    setSaving(true)
    try {
      await updateLayout(activeLayoutId, { grid_size: gridSize, slots })
      toast('View updated', 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to update view', 'error')
    } finally {
      setSaving(false)
    }
  }, [activeLayoutId, gridSize, slots, updateLayout, toast])

  // Delete the active layout
  const handleDelete = useCallback(async () => {
    if (!activeLayoutId) return
    const layout = layouts.find(l => l.id === activeLayoutId)
    if (!layout) return
    setSaving(true)
    try {
      await deleteLayout(activeLayoutId)
      setActiveLayoutId(null)
      toast(`View "${layout.name}" deleted`, 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete view', 'error')
    } finally {
      setSaving(false)
    }
  }, [activeLayoutId, layouts, deleteLayout, toast])

  // Set / clear default
  const handleSetDefault = useCallback(async (scope: 'user' | 'org') => {
    if (!activeLayoutId || !profile) return
    setSaving(true)
    try {
      await setDefault(site.id, activeLayoutId, scope, profile.id)
      toast(scope === 'org' ? 'Site default set' : 'Your default set', 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to set default', 'error')
    } finally {
      setSaving(false)
    }
  }, [activeLayoutId, profile, site.id, setDefault, toast])

  const handleClearDefault = useCallback(async (scope: 'user' | 'org') => {
    if (!profile) return
    setSaving(true)
    try {
      await clearDefault(site.id, scope, profile.id)
      toast(scope === 'org' ? 'Site default cleared' : 'Your default cleared', 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to clear default', 'error')
    } finally {
      setSaving(false)
    }
  }, [profile, site.id, clearDefault, toast])

  const activeLayout = layouts.find(l => l.id === activeLayoutId)
  const canManageActive = activeLayout && profile && (
    activeLayout.created_by === profile.id ||
    profile.role === 'rv_admin' ||
    (profile.role === 'org_admin' && activeLayout.visibility === 'org')
  )
  const isOrgAdmin = profile && (profile.role === 'org_admin' || profile.role === 'rv_admin')

  // Resolve current defaults
  const userDefault = defaults.find(d => d.user_id === profile?.id)
  const orgDefault = defaults.find(d => d.user_id === null)

  // Split layouts into personal and shared
  const personalLayouts = layouts.filter(l => l.visibility === 'personal')
  const sharedLayouts = layouts.filter(l => l.visibility === 'org')

  const gridSizes = LAYOUT_PRESETS

  // Helper to render default badge next to layout name in dropdown
  const layoutLabel = (l: { id: string; name: string }) => {
    const tags: string[] = []
    if (orgDefault?.layout_id === l.id) tags.push('site default')
    if (userDefault?.layout_id === l.id) tags.push('my default')
    return tags.length ? `${l.name} (${tags.join(', ')})` : l.name
  }

  return (
    <div className="flex h-full">
      {/* Main viewer area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Toolbar */}
        <div className="flex items-center gap-2 px-3 py-2 bg-sidebar border-b border-card-border flex-wrap">
          {/* View selector */}
          <span className="text-xs text-gray-500">View</span>
          <select
            value={activeLayoutId ?? ''}
            onChange={(e) => {
              if (e.target.value) {
                applyLayout(e.target.value)
              } else {
                setActiveLayoutId(null)
              }
            }}
            className="text-xs px-2 py-1 rounded border border-card-border bg-content text-gray-300 max-w-[200px]"
          >
            <option value="">Unsaved</option>
            {personalLayouts.length > 0 && (
              <optgroup label="Personal">
                {personalLayouts.map(l => (
                  <option key={l.id} value={l.id}>{layoutLabel(l)}</option>
                ))}
              </optgroup>
            )}
            {sharedLayouts.length > 0 && (
              <optgroup label="Shared">
                {sharedLayouts.map(l => (
                  <option key={l.id} value={l.id}>{layoutLabel(l)}</option>
                ))}
              </optgroup>
            )}
          </select>

          <button
            onClick={() => { setShowSaveForm(true); setSaveName('') }}
            className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors"
          >
            Save As...
          </button>

          {activeLayoutId && canManageActive && (
            <>
              <button
                onClick={handleUpdate}
                disabled={saving}
                className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors disabled:opacity-50"
              >
                Update
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="text-xs px-2 py-1 rounded text-gray-500 hover:text-error transition-colors disabled:opacity-50"
              >
                Delete
              </button>
            </>
          )}

          {/* Default controls — only shown when a saved view is selected */}
          {activeLayoutId && profile && (
            <>
              <div className="w-px h-4 bg-card-border mx-1" />
              {/* Personal default toggle */}
              {userDefault?.layout_id === activeLayoutId ? (
                <button
                  onClick={() => handleClearDefault('user')}
                  disabled={saving}
                  className="text-xs px-2 py-1 rounded bg-brand/20 text-brand transition-colors disabled:opacity-50"
                  title="This is your default view for this site. Click to clear."
                >
                  ★ My Default
                </button>
              ) : (
                <button
                  onClick={() => handleSetDefault('user')}
                  disabled={saving}
                  className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors disabled:opacity-50"
                  title="Set as your default view for this site"
                >
                  ☆ Set My Default
                </button>
              )}
              {/* Org default toggle — org admins only */}
              {isOrgAdmin && (
                orgDefault?.layout_id === activeLayoutId ? (
                  <button
                    onClick={() => handleClearDefault('org')}
                    disabled={saving}
                    className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 transition-colors disabled:opacity-50"
                    title="This is the site default for all users. Click to clear."
                  >
                    ★ Site Default
                  </button>
                ) : (
                  <button
                    onClick={() => handleSetDefault('org')}
                    disabled={saving}
                    className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors disabled:opacity-50"
                    title="Set as default view for all users on this site"
                  >
                    ☆ Set Site Default
                  </button>
                )
              )}
            </>
          )}

          <div className="w-px h-4 bg-card-border mx-1" />

          <span className="text-xs text-gray-500">Layout</span>
          {gridSizes.map((preset) => (
            <button
              key={preset.id}
              onClick={() => setGridSize(preset.id)}
              className={`text-xs px-2.5 py-1 rounded transition-colors ${
                gridSize === preset.id
                  ? 'bg-brand text-white'
                  : 'bg-gray-100 text-gray-500 hover:text-gray-900'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Save form */}
        {showSaveForm && (
          <div className="flex items-center gap-2 px-3 py-2 bg-sidebar/80 border-b border-card-border">
            <input
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveNew()}
              placeholder="View name..."
              className="text-xs px-2 py-1 rounded border border-card-border bg-content text-gray-300 w-40"
              autoFocus
            />
            <select
              value={saveVisibility}
              onChange={(e) => setSaveVisibility(e.target.value as 'personal' | 'org')}
              className="text-xs px-2 py-1 rounded border border-card-border bg-content text-gray-300"
            >
              <option value="personal">Personal</option>
              <option value="org">Shared (Org)</option>
            </select>
            <button
              onClick={handleSaveNew}
              disabled={!saveName.trim() || saving}
              className="text-xs px-2.5 py-1 rounded bg-brand text-white disabled:opacity-50"
            >
              {saving ? '...' : 'Save'}
            </button>
            <button
              onClick={() => setShowSaveForm(false)}
              className="text-xs text-gray-500 hover:text-gray-300"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Grid */}
        <div
          className="flex-1 min-h-0 p-2 gap-2"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${layout.cols}, 1fr)`,
            gridTemplateRows: `repeat(${layout.rows}, 1fr)`,
          }}
        >
          {Array.from({ length: totalSlots }, (_, i) => {
            const cameraId = slots[i]
            const camera = cameraId ? cameraMap.get(cameraId) ?? null : null
            const slotStyle = layout.slotStyles?.[i]
            return (
              <StreamSlot
                key={i}
                index={i}
                camera={camera}
                isSelected={selectedSlot === i}
                onSelect={() => setSelectedSlot(i)}
                onClear={() => clearSlot(i)}
                onToggleHd={handleToggleHd}
                onPtzOpen={onPtzOpen}
                onRecordingOpen={onRecordingOpen}
                onEditCamera={onEditCamera}
                onSettingsOpen={onSettingsOpen}
                onDrop={(camId) => assignCamera(camId, i)}
                style={slotStyle}
              />
            )
          })}
        </div>
      </div>

      {/* Sidebar */}
      <StreamSidebar
        cameras={cameras}
        assignedCameraIds={assignedCameraIds}
        onAssign={(camId) => assignCamera(camId)}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((p) => !p)}
      />
    </div>
  )
}
